import DashboardContainer from "components/dashboard/DashboardContainer";
import { Divider } from "components/design/Divider";
import { Button, ButtonProps } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { Header } from "components/DesignSystem/Header/Header";
import { Search } from "components/DesignSystem/Search/Search";
import { Icons, SvgIcons } from "components/icons/Icons";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import React, { useMemo, useState } from "react";
import { debounce } from "utils/debouncing";

const SvgIconsGallery = ({
  searchText,
  iconSize,
}: {
  searchText: string | null;
  iconSize: ButtonProps["size"];
}) => {
  const { successToast, alertToast } = useToast();

  const filteredSvgIcons = useMemo(() => {
    return Object.entries(SvgIcons)
      .sort(([a], [b]) => a.localeCompare(b))
      .filter(([iconName]) =>
        iconName.toLowerCase().includes(searchText?.toLowerCase() || "")
      );
  }, [searchText]);

  const handleClick = async (fileName: string, path: string) => {
    const text = `<img src={${fileName}} alt="${fileName}" className="t-size-10" />`;
    const importText = `import ${fileName} from "${path}";\n\n${text}`;

    try {
      await navigator.clipboard.writeText(importText);
      successToast({
        title: importText,
        duration: 5000,
      });
    } catch (error) {
      alertToast({
        title: "Failed to copy to clipboard",
      });
    }
  };

  return (
    <div className="t-w-full t-flex t-flex-col t-gap-2">
      <Header
        title={
          <div className="t-flex t-items-center t-gap-2">
            SVG Icons
            <div className="t-text-body-sm t-text-neutral-500">
              {filteredSvgIcons.length} of {Object.keys(SvgIcons).length}
            </div>
          </div>
        }
        v2
        bottom={
          <div>
            <p className="t-text-body-sm t-text-neutral-500 t-m-0">
              Click on an icon to copy its import statement.
            </p>
            <p className="t-text-body-sm t-text-neutral-500">
              SVG icons are imported from <code>static/images</code>
            </p>
          </div>
        }
      />
      <div className="t-flex t-flex-wrap t-gap-4 t-items-center t-justify-center t-overflow-hidden">
        {filteredSvgIcons.map(([iconName, { src, path }]) => (
          <Button
            title={path}
            customType="icon"
            size={iconSize}
            key={iconName}
            onClick={() => handleClick(iconName, path)}
          >
            <img
              src={src}
              alt={iconName}
              className="t-w-full t-h-full t-object-contain"
              key={iconName}
            />
          </Button>
        ))}
      </div>
    </div>
  );
};

const IconsGallery = ({
  searchText,
  iconSize,
}: {
  searchText: string | null;
  iconSize: ButtonProps["size"];
}) => {
  const { successToast, alertToast } = useToast();

  const filteredIcons = useMemo(() => {
    return Object.entries(Icons)
      .sort(([a], [b]) => a.localeCompare(b))
      .filter(([iconName]) =>
        iconName.toLowerCase().includes(searchText?.toLowerCase() || "")
      );
  }, [searchText]);

  const handleClick = async (fileName: string, path: string) => {
    const text = `<${fileName} />`;
    const importText = `import { ${fileName} } from "components/icons/${path}";\n\n${text}`;

    try {
      await navigator.clipboard.writeText(importText);
      successToast({
        title: importText,
        duration: 5000,
      });
    } catch (error) {
      alertToast({
        title: "Failed to copy to clipboard",
      });
    }
  };

  return (
    <div className="t-w-full t-flex t-flex-col t-gap-2">
      <Header
        title={
          <div className="t-flex t-items-center t-gap-2">
            Icons
            <div className="t-text-body-sm t-text-neutral-500">
              {filteredIcons.length} of {Object.keys(Icons).length}
            </div>
          </div>
        }
        v2
        bottom={
          <>
            <p className="t-text-body-sm t-text-neutral-500 t-m-0">
              Click on an icon to copy its import statement.
            </p>
            <p className="t-text-body-sm t-text-neutral-500">
              Icons are imported from <code>components/icons</code>
            </p>
          </>
        }
      />

      <div className="t-flex t-flex-wrap t-gap-4 t-items-center t-justify-center t-overflow-hidden">
        {filteredIcons.map(([iconName, { component: IconComponent, path }]) => (
          <Button
            title={path}
            customType="icon"
            size={iconSize}
            key={iconName}
            onClick={() => handleClick(iconName, path)}
          >
            <IconComponent />
          </Button>
        ))}
      </div>
    </div>
  );
};

export const IconPage = () => {
  const query = useQuery();
  const searchText = query.get("search");
  const { update: updateQuery } = useUpdateQuery();
  const [iconSize, setIconSize] = useState<ButtonProps["size"]>("regular");

  const handleSearchChange = debounce(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateQuery({ query: "search", value: e.target.value || null });
    }
  );

  return (
    <DashboardContainer className="t-px-10 t-pb-4">
      <DashboardContainer.Header sticky className="t-z-sidebar">
        <div className="t-flex t-w-full t-justify-between t-items-center t-my-4 t-z-header">
          <div>
            <Search
              autoFocus
              onChange={handleSearchChange}
              placeholder="Search..."
            />
          </div>

          <div className="t-w-52">
            <Combobox
              size="small"
              placeholder="Select size"
              defaultValue={{ label: "Regular", value: "regular" }}
              onChange={(option) => {
                if (option instanceof Array) {
                  return;
                }
                setIconSize(option?.value as ButtonProps["size"]);
              }}
              isClearable={false}
              options={[
                { label: "Small", value: "small" },
                { label: "Regular", value: "regular" },
                { label: "Large", value: "large" },
              ]}
            />
          </div>
        </div>
      </DashboardContainer.Header>
      <DashboardContainer.Content className="t-flex t-flex-col t-gap-8">
        <IconsGallery iconSize={iconSize} searchText={searchText} />
        <Divider />
        <SvgIconsGallery searchText={searchText} iconSize={iconSize} />
      </DashboardContainer.Content>
    </DashboardContainer>
  );
};
